// Build CRM/social deep links for a contact FE-side from the raw identifiers.

import { apiFetch } from './api'
import { getSalesforceUrl, preloadSalesforceBaseUrl } from './salesforce'
import type { CallerQueueCrmIds } from './dialerApi'

let hubspotPortalId: number | null = null
let pipedriveDomain: string | null = null
let configLoaded = false
let loadPromise: Promise<void> | null = null

/**
 * Fetch + cache the tenant connection metadata needed to build CRM deep links
 * (Salesforce base URL, HubSpot portal id, Pipedrive subdomain). Best-effort and
 * idempotent — failures leave the relevant builder returning null so its chip is
 * simply hidden. Call once (e.g. on view mount) before building chips.
 */
export async function preloadCrmLinkConfig(): Promise<void> {
  if (configLoaded) return
  if (loadPromise) return loadPromise
  loadPromise = (async () => {
    await Promise.allSettled([
      preloadSalesforceBaseUrl(),
      apiFetch('/user/integrations/hubspot')
        .then((r: any) => { hubspotPortalId = r?.hub_id ?? null })
        .catch(() => { hubspotPortalId = null }),
      apiFetch('/user/integrations/pipedrive')
        .then((r: any) => { pipedriveDomain = (r?.company_domain || null) })
        .catch(() => { pipedriveDomain = null }),
    ])
    configLoaded = true
    loadPromise = null
  })()
  return loadPromise
}

/**
 * Deep link to review a logged Call engagement in HubSpot:
 * `https://app.hubspot.com/calls/{portalId}/review/{engagementId}`. Returns null when the portal id
 * isn't loaded (so the caller renders "Synced" as plain text rather than a dead link). Requires
 * `preloadCrmLinkConfig()` to have run.
 */
export function hubspotCallReviewUrl(engagementId: string | null | undefined): string | null {
  if (!engagementId || !hubspotPortalId) return null
  return `https://app.hubspot.com/calls/${hubspotPortalId}/review/${engagementId}`
}

/**
 * Deep link to a logged call's Salesforce Task record (Lightning):
 * `{base_url}/lightning/r/Task/{taskId}/view`. Returns null when the SF base URL isn't loaded or there's
 * no task id (so the caller renders "Synced" as plain text). Requires `preloadCrmLinkConfig()` to have run.
 */
export function salesforceTaskReviewUrl(taskId: string | null | undefined): string | null {
  if (!taskId) return null
  return getSalesforceUrl({ Id: taskId }, 'Task')
}

/**
 * Build a HubSpot record URL. Returns null if portal ID not yet loaded.
 * objectType: 'deal', 'contact', 'company'
 */
export function getHubspotUrl(objectType: string, recordId: string | number | null | undefined): string | null {
  if (!hubspotPortalId || !recordId) return null
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/${objectType}/${recordId}`
}

/**
 * Build a Pipedrive record URL. Returns null if domain not yet loaded.
 * objectType: 'deal', 'person', 'organization'
 */
export function getPipedriveUrl(objectType: string, recordId: string | number | null | undefined): string | null {
  if (!pipedriveDomain || !recordId) return null
  return `https://${pipedriveDomain}.pipedrive.com/${objectType}/${recordId}`
}

export interface CrmChip {
  key: string
  label: string
  href: string
  /** Domain used by FavIcon to render the brand glyph. */
  domain: string
}

/**
 * Build the ordered list of CRM/social chips for a contact. A chip is included
 * only when its link can actually be constructed (id present AND, where needed,
 * the cached connection metadata is available). Order matches the mockups:
 * LinkedIn, Salesforce, HubSpot, Pipedrive, Outreach.
 */
export function buildCrmChips(ids: CallerQueueCrmIds | null | undefined): CrmChip[] {
  if (!ids) return []
  const chips: CrmChip[] = []

  const linkedin = String(ids.linkedin_url || '').trim()
  if (linkedin) {
    chips.push({ key: 'linkedin', label: 'LinkedIn', href: linkedin, domain: 'linkedin.com' })
  }

  // Salesforce — prefer the Contact record, fall back to the Lead record.
  const sfHref = ids.salesforce_contact_id
    ? getSalesforceUrl({ Id: ids.salesforce_contact_id }, 'Contact')
    : ids.salesforce_lead_id
      ? getSalesforceUrl({ Id: ids.salesforce_lead_id }, 'Lead')
      : null
  if (sfHref) {
    chips.push({ key: 'salesforce', label: 'Salesforce', href: sfHref, domain: 'salesforce.com' })
  }

  const hsHref = getHubspotUrl('contact', ids.hubspot_contact_id)
  if (hsHref) {
    chips.push({ key: 'hubspot', label: 'HubSpot', href: hsHref, domain: 'hubspot.com' })
  }

  const pdHref = getPipedriveUrl('person', ids.pipedrive_person_id)
  if (pdHref) {
    chips.push({ key: 'pipedrive', label: 'Pipedrive', href: pdHref, domain: 'pipedrive.com' })
  }

  if (ids.outreach_prospect_id) {
    chips.push({
      key: 'outreach',
      label: 'Outreach',
      href: `https://app.outreach.io/prospect/${ids.outreach_prospect_id}`,
      domain: 'outreach.io',
    })
  }

  return chips
}
